/**
 * Measurement Converter — grams ↔ cups / ounces / teaspoons
 *
 * Comprehensive conversion database for cooking ingredients.
 * When a specific ingredient density is unknown, generic ratios are used.
 */

export type MeasureUnit = "metric" | "cups" | "ounces";

interface ConversionEntry {
  /** grams per cup */
  gramsPerCup: number;
  /** grams per tablespoon (for small amounts) */
  gramsPerTbsp: number;
}

// ─── Ingredient-specific densities (grams per cup / per tbsp) ───
const INGREDIENT_DENSITIES: Record<string, ConversionEntry> = {
  // ── Flours & starches ──
  harina:                { gramsPerCup: 125, gramsPerTbsp: 8 },
  flour:                 { gramsPerCup: 125, gramsPerTbsp: 8 },
  "harina de almendra":  { gramsPerCup: 96,  gramsPerTbsp: 6 },
  "almond flour":        { gramsPerCup: 96,  gramsPerTbsp: 6 },
  "harina de coco":      { gramsPerCup: 112, gramsPerTbsp: 7 },
  "coconut flour":       { gramsPerCup: 112, gramsPerTbsp: 7 },
  "harina integral":     { gramsPerCup: 130, gramsPerTbsp: 8 },
  "whole wheat flour":   { gramsPerCup: 130, gramsPerTbsp: 8 },
  "harina de avena":     { gramsPerCup: 100, gramsPerTbsp: 6.5 },
  "oat flour":           { gramsPerCup: 100, gramsPerTbsp: 6.5 },
  "harina de arroz":     { gramsPerCup: 158, gramsPerTbsp: 10 },
  "rice flour":          { gramsPerCup: 158, gramsPerTbsp: 10 },
  maicena:               { gramsPerCup: 128, gramsPerTbsp: 8 },
  "fécula de maíz":      { gramsPerCup: 128, gramsPerTbsp: 8 },
  cornstarch:            { gramsPerCup: 128, gramsPerTbsp: 8 },
  almidón:               { gramsPerCup: 128, gramsPerTbsp: 8 },
  "harina de garbanzo":  { gramsPerCup: 90,  gramsPerTbsp: 6 },
  "chickpea flour":      { gramsPerCup: 90,  gramsPerTbsp: 6 },

  // ── Oats & cereals ──
  avena:                 { gramsPerCup: 90,  gramsPerTbsp: 6 },
  oats:                  { gramsPerCup: 90,  gramsPerTbsp: 6 },
  granola:               { gramsPerCup: 110, gramsPerTbsp: 7 },

  // ── Sugars & sweeteners ──
  azúcar:                { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  azucar:                { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  sugar:                 { gramsPerCup: 200, gramsPerTbsp: 12.5 },
  "azúcar glass":        { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "powdered sugar":      { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "azúcar morena":       { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "brown sugar":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "azúcar mascabado":    { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  piloncillo:            { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  eritritol:             { gramsPerCup: 180, gramsPerTbsp: 11 },
  erythritol:            { gramsPerCup: 180, gramsPerTbsp: 11 },
  stevia:                { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  estevia:               { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  xilitol:               { gramsPerCup: 190, gramsPerTbsp: 12 },
  xylitol:               { gramsPerCup: 190, gramsPerTbsp: 12 },
  "monk fruit":          { gramsPerCup: 150, gramsPerTbsp: 9.5 },
  "fruto del monje":     { gramsPerCup: 150, gramsPerTbsp: 9.5 },
  miel:                  { gramsPerCup: 340, gramsPerTbsp: 21 },
  honey:                 { gramsPerCup: 340, gramsPerTbsp: 21 },
  "jarabe de arce":      { gramsPerCup: 312, gramsPerTbsp: 20 },
  "maple syrup":         { gramsPerCup: 312, gramsPerTbsp: 20 },
  "jarabe de agave":     { gramsPerCup: 336, gramsPerTbsp: 21 },
  agave:                 { gramsPerCup: 336, gramsPerTbsp: 21 },
  melaza:                { gramsPerCup: 340, gramsPerTbsp: 21 },
  molasses:              { gramsPerCup: 340, gramsPerTbsp: 21 },

  // ── Fats & oils ──
  mantequilla:           { gramsPerCup: 227, gramsPerTbsp: 14 },
  butter:                { gramsPerCup: 227, gramsPerTbsp: 14 },
  margarina:             { gramsPerCup: 227, gramsPerTbsp: 14 },
  margarine:             { gramsPerCup: 227, gramsPerTbsp: 14 },
  aceite:                { gramsPerCup: 218, gramsPerTbsp: 14 },
  oil:                   { gramsPerCup: 218, gramsPerTbsp: 14 },
  "aceite de oliva":     { gramsPerCup: 216, gramsPerTbsp: 13.5 },
  "olive oil":           { gramsPerCup: 216, gramsPerTbsp: 13.5 },
  "aceite de coco":      { gramsPerCup: 218, gramsPerTbsp: 14 },
  "coconut oil":         { gramsPerCup: 218, gramsPerTbsp: 14 },
  manteca:               { gramsPerCup: 205, gramsPerTbsp: 13 },
  lard:                  { gramsPerCup: 205, gramsPerTbsp: 13 },
  ghee:                  { gramsPerCup: 218, gramsPerTbsp: 14 },

  // ── Dairy & alt milks ──
  leche:                 { gramsPerCup: 244, gramsPerTbsp: 15 },
  milk:                  { gramsPerCup: 244, gramsPerTbsp: 15 },
  "leche de almendra":   { gramsPerCup: 240, gramsPerTbsp: 15 },
  "almond milk":         { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche de coco":       { gramsPerCup: 240, gramsPerTbsp: 15 },
  "coconut milk":        { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche de avena":      { gramsPerCup: 240, gramsPerTbsp: 15 },
  "oat milk":            { gramsPerCup: 240, gramsPerTbsp: 15 },
  "leche evaporada":     { gramsPerCup: 252, gramsPerTbsp: 16 },
  "evaporated milk":     { gramsPerCup: 252, gramsPerTbsp: 16 },
  "leche condensada":    { gramsPerCup: 306, gramsPerTbsp: 19 },
  "condensed milk":      { gramsPerCup: 306, gramsPerTbsp: 19 },
  crema:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  cream:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  "crema agria":         { gramsPerCup: 230, gramsPerTbsp: 14.5 },
  "sour cream":          { gramsPerCup: 230, gramsPerTbsp: 14.5 },
  yogur:                 { gramsPerCup: 245, gramsPerTbsp: 15 },
  yogurt:                { gramsPerCup: 245, gramsPerTbsp: 15 },
  "queso crema":         { gramsPerCup: 232, gramsPerTbsp: 14.5 },
  "cream cheese":        { gramsPerCup: 232, gramsPerTbsp: 14.5 },
  queso:                 { gramsPerCup: 113, gramsPerTbsp: 7 },
  cheese:                { gramsPerCup: 113, gramsPerTbsp: 7 },
  ricotta:               { gramsPerCup: 246, gramsPerTbsp: 15 },
  mascarpone:            { gramsPerCup: 227, gramsPerTbsp: 14 },

  // ── Cocoa & chocolate ──
  cacao:                 { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  cocoa:                 { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  "cocoa powder":        { gramsPerCup: 86,  gramsPerTbsp: 5.4 },
  "chocolate chips":     { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  "chispas de chocolate":{ gramsPerCup: 170, gramsPerTbsp: 10.6 },
  chocolate:             { gramsPerCup: 170, gramsPerTbsp: 10.6 },

  // ── Nuts & seeds ──
  nueces:                { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  walnuts:               { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  almendras:             { gramsPerCup: 143, gramsPerTbsp: 9 },
  almonds:               { gramsPerCup: 143, gramsPerTbsp: 9 },
  cacahuate:             { gramsPerCup: 146, gramsPerTbsp: 9 },
  cacahuates:            { gramsPerCup: 146, gramsPerTbsp: 9 },
  peanuts:               { gramsPerCup: 146, gramsPerTbsp: 9 },
  maní:                  { gramsPerCup: 146, gramsPerTbsp: 9 },
  pistachos:             { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  pistachios:            { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  avellanas:             { gramsPerCup: 135, gramsPerTbsp: 8.4 },
  hazelnuts:             { gramsPerCup: 135, gramsPerTbsp: 8.4 },
  pecanas:               { gramsPerCup: 109, gramsPerTbsp: 6.8 },
  pecans:                { gramsPerCup: 109, gramsPerTbsp: 6.8 },
  "nuez de castilla":    { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "nuez de la india":    { gramsPerCup: 137, gramsPerTbsp: 8.5 },
  cashews:               { gramsPerCup: 137, gramsPerTbsp: 8.5 },
  "nuez de macadamia":   { gramsPerCup: 134, gramsPerTbsp: 8.4 },
  macadamia:             { gramsPerCup: 134, gramsPerTbsp: 8.4 },
  "semillas de girasol": { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "sunflower seeds":     { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "semillas de calabaza":{ gramsPerCup: 129, gramsPerTbsp: 8 },
  "pumpkin seeds":       { gramsPerCup: 129, gramsPerTbsp: 8 },
  pepitas:               { gramsPerCup: 129, gramsPerTbsp: 8 },
  "pine nuts":           { gramsPerCup: 135, gramsPerTbsp: 8.5 },
  piñones:               { gramsPerCup: 135, gramsPerTbsp: 8.5 },

  // ── Seeds (small) ──
  chía:                  { gramsPerCup: 168, gramsPerTbsp: 10.5 },
  chia:                  { gramsPerCup: 168, gramsPerTbsp: 10.5 },
  linaza:                { gramsPerCup: 155, gramsPerTbsp: 10 },
  linseed:               { gramsPerCup: 155, gramsPerTbsp: 10 },
  flaxseed:              { gramsPerCup: 155, gramsPerTbsp: 10 },
  "semillas de lino":    { gramsPerCup: 155, gramsPerTbsp: 10 },
  ajonjolí:              { gramsPerCup: 144, gramsPerTbsp: 9 },
  "sesame seeds":        { gramsPerCup: 144, gramsPerTbsp: 9 },
  sésamo:                { gramsPerCup: 144, gramsPerTbsp: 9 },
  amapola:               { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  "poppy seeds":         { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  hemp:                  { gramsPerCup: 160, gramsPerTbsp: 10 },
  "semillas de cáñamo":  { gramsPerCup: 160, gramsPerTbsp: 10 },

  // ── Nut & seed butters ──
  "crema de cacahuate":  { gramsPerCup: 258, gramsPerTbsp: 16 },
  "peanut butter":       { gramsPerCup: 258, gramsPerTbsp: 16 },
  "crema de almendra":   { gramsPerCup: 256, gramsPerTbsp: 16 },
  "almond butter":       { gramsPerCup: 256, gramsPerTbsp: 16 },
  tahini:                { gramsPerCup: 240, gramsPerTbsp: 15 },

  // ── Grains & legumes ──
  arroz:                 { gramsPerCup: 185, gramsPerTbsp: 12 },
  rice:                  { gramsPerCup: 185, gramsPerTbsp: 12 },
  quinoa:                { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  quinua:                { gramsPerCup: 170, gramsPerTbsp: 10.6 },
  cuscús:                { gramsPerCup: 173, gramsPerTbsp: 10.8 },
  couscous:              { gramsPerCup: 173, gramsPerTbsp: 10.8 },
  lenteja:               { gramsPerCup: 192, gramsPerTbsp: 12 },
  lentejas:              { gramsPerCup: 192, gramsPerTbsp: 12 },
  lentils:               { gramsPerCup: 192, gramsPerTbsp: 12 },
  frijol:                { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  frijoles:              { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  beans:                 { gramsPerCup: 184, gramsPerTbsp: 11.5 },
  garbanzo:              { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  garbanzos:             { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  chickpeas:             { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  "chícharos":           { gramsPerCup: 160, gramsPerTbsp: 10 },
  peas:                  { gramsPerCup: 160, gramsPerTbsp: 10 },
  amaranto:              { gramsPerCup: 195, gramsPerTbsp: 12.2 },
  amaranth:              { gramsPerCup: 195, gramsPerTbsp: 12.2 },
  trigo:                 { gramsPerCup: 180, gramsPerTbsp: 11.3 },

  // ── Dried fruits ──
  pasas:                 { gramsPerCup: 145, gramsPerTbsp: 9 },
  raisins:               { gramsPerCup: 145, gramsPerTbsp: 9 },
  arándanos:             { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  cranberries:           { gramsPerCup: 140, gramsPerTbsp: 8.8 },
  dátiles:               { gramsPerCup: 147, gramsPerTbsp: 9.2 },
  dates:                 { gramsPerCup: 147, gramsPerTbsp: 9.2 },
  "coco rallado":        { gramsPerCup: 85,  gramsPerTbsp: 5.3 },
  "shredded coconut":    { gramsPerCup: 85,  gramsPerTbsp: 5.3 },
  coco:                  { gramsPerCup: 85,  gramsPerTbsp: 5.3 },

  // ── Fresh produce (common weighed items) ──
  espinaca:              { gramsPerCup: 30,  gramsPerTbsp: 2 },
  espinacas:             { gramsPerCup: 30,  gramsPerTbsp: 2 },
  spinach:               { gramsPerCup: 30,  gramsPerTbsp: 2 },
  tomate:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  tomato:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  jitomate:              { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  cebolla:               { gramsPerCup: 160, gramsPerTbsp: 10 },
  onion:                 { gramsPerCup: 160, gramsPerTbsp: 10 },
  ajo:                   { gramsPerCup: 136, gramsPerTbsp: 8.5 },
  garlic:                { gramsPerCup: 136, gramsPerTbsp: 8.5 },
  zanahoria:             { gramsPerCup: 128, gramsPerTbsp: 8 },
  carrot:                { gramsPerCup: 128, gramsPerTbsp: 8 },
  calabaza:              { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  pumpkin:               { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  calabacita:            { gramsPerCup: 124, gramsPerTbsp: 7.8 },
  zucchini:              { gramsPerCup: 124, gramsPerTbsp: 7.8 },
  pepino:                { gramsPerCup: 119, gramsPerTbsp: 7.4 },
  cucumber:              { gramsPerCup: 119, gramsPerTbsp: 7.4 },
  pimiento:              { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  pimientos:             { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  "bell pepper":         { gramsPerCup: 149, gramsPerTbsp: 9.3 },
  champiñones:           { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  mushrooms:             { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  hongos:                { gramsPerCup: 70,  gramsPerTbsp: 4.4 },
  brócoli:               { gramsPerCup: 91,  gramsPerTbsp: 5.7 },
  broccoli:              { gramsPerCup: 91,  gramsPerTbsp: 5.7 },
  coliflor:              { gramsPerCup: 100, gramsPerTbsp: 6.3 },
  cauliflower:           { gramsPerCup: 100, gramsPerTbsp: 6.3 },
  elote:                 { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  maíz:                  { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  corn:                  { gramsPerCup: 164, gramsPerTbsp: 10.3 },
  papa:                  { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  potato:                { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  camote:                { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  "sweet potato":        { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  batata:                { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  boniato:               { gramsPerCup: 133, gramsPerTbsp: 8.3 },
  yuca:                  { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  cassava:               { gramsPerCup: 165, gramsPerTbsp: 10.3 },

  // ── Fruits ──
  fresa:                 { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  fresas:                { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  strawberries:          { gramsPerCup: 152, gramsPerTbsp: 9.5 },
  arándano:              { gramsPerCup: 148, gramsPerTbsp: 9.3 },
  blueberries:           { gramsPerCup: 148, gramsPerTbsp: 9.3 },
  frambuesa:             { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  frambuesas:            { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  raspberries:           { gramsPerCup: 123, gramsPerTbsp: 7.7 },
  manzana:               { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  apple:                 { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  plátano:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  platano:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  banana:                { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  mango:                 { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  piña:                  { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  pineapple:             { gramsPerCup: 165, gramsPerTbsp: 10.3 },
  naranja:               { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  orange:                { gramsPerCup: 180, gramsPerTbsp: 11.3 },
  durazno:               { gramsPerCup: 154, gramsPerTbsp: 9.6 },
  peach:                 { gramsPerCup: 154, gramsPerTbsp: 9.6 },
  aguacate:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  avocado:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },

  // ── Spices & seasonings (small amounts) ──
  sal:                   { gramsPerCup: 288, gramsPerTbsp: 18 },
  salt:                  { gramsPerCup: 288, gramsPerTbsp: 18 },
  canela:                { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  cinnamon:              { gramsPerCup: 125, gramsPerTbsp: 7.8 },
  pimienta:              { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  pepper:                { gramsPerCup: 116, gramsPerTbsp: 7.3 },
  comino:                { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  cumin:                 { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  jengibre:              { gramsPerCup: 96,  gramsPerTbsp: 6 },
  ginger:                { gramsPerCup: 96,  gramsPerTbsp: 6 },
  cúrcuma:               { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  turmeric:              { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  paprika:               { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  pimentón:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  "chile en polvo":      { gramsPerCup: 115, gramsPerTbsp: 7.2 },
  "chili powder":        { gramsPerCup: 115, gramsPerTbsp: 7.2 },
  orégano:               { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  oregano:               { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  albahaca:              { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  basil:                 { gramsPerCup: 24,  gramsPerTbsp: 1.5 },
  cilantro:              { gramsPerCup: 16,  gramsPerTbsp: 1 },
  perejil:               { gramsPerCup: 30,  gramsPerTbsp: 1.9 },
  parsley:               { gramsPerCup: 30,  gramsPerTbsp: 1.9 },
  romero:                { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  rosemary:              { gramsPerCup: 20,  gramsPerTbsp: 1.3 },
  tomillo:               { gramsPerCup: 18,  gramsPerTbsp: 1.1 },
  thyme:                 { gramsPerCup: 18,  gramsPerTbsp: 1.1 },

  // ── Baking ──
  "polvo para hornear":  { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "baking powder":       { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "bicarbonato":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "baking soda":         { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  levadura:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  yeast:                 { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  gelatina:              { gramsPerCup: 150, gramsPerTbsp: 9.4 },
  gelatin:               { gramsPerCup: 150, gramsPerTbsp: 9.4 },

  // ── Proteins ──
  "proteína en polvo":   { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  "protein powder":      { gramsPerCup: 120, gramsPerTbsp: 7.5 },
  colágeno:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },
  collagen:              { gramsPerCup: 110, gramsPerTbsp: 6.9 },

  // ── Sauces & condiments ──
  "salsa de soya":       { gramsPerCup: 255, gramsPerTbsp: 16 },
  "soy sauce":           { gramsPerCup: 255, gramsPerTbsp: 16 },
  vinagre:               { gramsPerCup: 240, gramsPerTbsp: 15 },
  vinegar:               { gramsPerCup: 240, gramsPerTbsp: 15 },
  mostaza:               { gramsPerCup: 250, gramsPerTbsp: 15.6 },
  mustard:               { gramsPerCup: 250, gramsPerTbsp: 15.6 },
  mayonesa:              { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  mayonnaise:            { gramsPerCup: 220, gramsPerTbsp: 13.8 },
  "pasta de tomate":     { gramsPerCup: 262, gramsPerTbsp: 16.4 },
  "tomato paste":        { gramsPerCup: 262, gramsPerTbsp: 16.4 },

  // ── Liquids ──
  agua:                  { gramsPerCup: 240, gramsPerTbsp: 15 },
  water:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  caldo:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  broth:                 { gramsPerCup: 240, gramsPerTbsp: 15 },
  café:                  { gramsPerCup: 240, gramsPerTbsp: 15 },
  coffee:                { gramsPerCup: 240, gramsPerTbsp: 15 },
  jugo:                  { gramsPerCup: 248, gramsPerTbsp: 15.5 },
  juice:                 { gramsPerCup: 248, gramsPerTbsp: 15.5 },
  vino:                  { gramsPerCup: 236, gramsPerTbsp: 14.8 },
  wine:                  { gramsPerCup: 236, gramsPerTbsp: 14.8 },

  // ── Misc ──
  "extracto de vainilla": { gramsPerCup: 208, gramsPerTbsp: 13 },
  "vanilla extract":     { gramsPerCup: 208, gramsPerTbsp: 13 },
  vainilla:              { gramsPerCup: 208, gramsPerTbsp: 13 },
  vanilla:               { gramsPerCup: 208, gramsPerTbsp: 13 },
};

const GENERIC: ConversionEntry = { gramsPerCup: 140, gramsPerTbsp: 9 };

const GRAMS_PER_OUNCE = 28.3495;
const GRAMS_PER_POUND = 453.592;
const OUNCES_PER_POUND = 16;
const TSP_PER_TBSP = 3;

function findDensity(ingredientText: string): ConversionEntry {
  const lower = ingredientText.toLowerCase();
  // Try longer keys first (more specific matches)
  const sortedKeys = Object.keys(INGREDIENT_DENSITIES).sort((a, b) => b.length - a.length);
  for (const key of sortedKeys) {
    if (lower.includes(key)) return INGREDIENT_DENSITIES[key];
  }
  return GENERIC;
}

function formatFraction(n: number): string {
  if (n >= 1) {
    const whole = Math.floor(n);
    const frac = n - whole;
    if (frac < 0.08) return `${whole}`;
    if (frac < 0.2) return `${whole > 0 ? whole + " " : ""}⅛`;
    if (frac < 0.29) return `${whole > 0 ? whole + " " : ""}¼`;
    if (frac < 0.42) return `${whole > 0 ? whole + " " : ""}⅓`;
    if (frac < 0.58) return `${whole > 0 ? whole + " " : ""}½`;
    if (frac < 0.71) return `${whole > 0 ? whole + " " : ""}⅔`;
    if (frac < 0.83) return `${whole > 0 ? whole + " " : ""}¾`;
    return `${whole + 1}`;
  }
  if (n < 0.08) return "⅛";
  if (n < 0.2) return "⅛";
  if (n < 0.29) return "¼";
  if (n < 0.42) return "⅓";
  if (n < 0.58) return "½";
  if (n < 0.71) return "⅔";
  if (n < 0.83) return "¾";
  return "1";
}

/**
 * Detect grams/kilograms in ingredient text and convert to target unit.
 */
export function convertIngredient(text: string, targetUnit: MeasureUnit): string {
  if (targetUnit === "metric") return text;

  // First normalize kilograms to grams so the rest of the pipeline only deals with grams.
  // Match kg / kgs / kilo / kilos / kilogramo / kilograms — but NOT "g" alone.
  const kgRegex = /(\d+(?:[.,]\d+)?)\s*(?:kg|kgs|kilogramos?|kilograms?|kilos?)\b/gi;
  const normalized = text.replace(kgRegex, (_match, numStr) => {
    const kg = parseFloat(numStr.replace(",", "."));
    if (isNaN(kg) || kg <= 0) return _match;
    const grams = kg * 1000;
    return `${grams % 1 === 0 ? grams : grams.toFixed(1)} g`;
  });

  const gramsRegex = /(\d+(?:[.,]\d+)?)\s*(?:gr(?:amos?)?|grams?|g)\b/gi;

  return normalized.replace(gramsRegex, (match, numStr) => {
    const grams = parseFloat(numStr.replace(",", "."));
    if (isNaN(grams) || grams <= 0) return match;

    const density = findDensity(text);

    switch (targetUnit) {
      case "ounces": {
        // ≥ 1 lb (≈454 g) → use pounds (with oz remainder if not clean)
        if (grams >= GRAMS_PER_POUND) {
          const totalOz = grams / GRAMS_PER_OUNCE;
          const lbs = totalOz / OUNCES_PER_POUND;
          // Round to 0.25 lb steps for cleanliness
          const roundedLbs = Math.round(lbs * 4) / 4;
          return roundedLbs % 1 === 0
            ? `${roundedLbs} lb`
            : `${roundedLbs} lb`;
        }
        const oz = grams / GRAMS_PER_OUNCE;
        return oz < 0.5
          ? `${formatFraction(oz)} oz`
          : `${Math.round(oz * 10) / 10} oz`;
      }
      case "cups": {
        const cups = grams / density.gramsPerCup;
        if (cups < 0.2) {
          const tbsp = grams / density.gramsPerTbsp;
          if (tbsp < 1) {
            const tsp = tbsp * TSP_PER_TBSP;
            return `${formatFraction(tsp)} tsp`;
          }
          return `${formatFraction(tbsp)} tbsp`;
        }
        return `${formatFraction(cups)} cup${cups > 1.1 ? "s" : ""}`;
      }
      default:
        return match;
    }
  });
}

/** Parse a numeric string: integer, decimal, simple fraction "1/2", or mixed number "1 1/2" */
function parseNum(s: string): number {
  // Mixed number: "1 1/2" → 1.5
  const mixed = s.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const den = parseInt(mixed[3], 10);
    return den !== 0 ? parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / den : NaN;
  }
  if (s.includes("/")) {
    const [a, b] = s.split("/").map(Number);
    return b !== 0 ? a / b : NaN;
  }
  return parseFloat(s.replace(",", "."));
}

/**
 * Normalize English unit strings produced by translateSpanishUnits before pattern matching.
 * "cup(s)" → "cups",  "glass(es)" → "glasses"  (the literal parentheses break \b assertions)
 */
function normalizeEnglishUnits(text: string): string {
  return text
    .replace(/cup\(s\)/gi, "cups")
    .replace(/glass\(es\)/gi, "glasses");
}

// [regex, ml per unit]
// Covers Spanish originals + English forms (abbreviated and full words)
// The capture group supports: plain integers (2), decimals (1.5), simple fractions (1/2),
// and mixed numbers (1 1/2) — e.g. "1 1/2 cucharada" was previously broken into
// orphaned "1" + converted "5.3 g" because only the fractional part was matched.
const SPANISH_VOLUME_PATTERNS: [RegExp, number][] = [
  [/((?:\d+\s+)?\d+\/\d+|\d+(?:[.,]\d+)?)\s*(?:tazas?|cups?)\b/gi,                                   240],
  [/((?:\d+\s+)?\d+\/\d+|\d+(?:[.,]\d+)?)\s*(?:vasos?|glasses?)\b/gi,                                240],
  [/((?:\d+\s+)?\d+\/\d+|\d+(?:[.,]\d+)?)\s*(?:cucharadas?|cdas?\.?|tablespoons?|tbsp)\b/gi,          15],
  [/((?:\d+\s+)?\d+\/\d+|\d+(?:[.,]\d+)?)\s*(?:cucharaditas?|cdtas?\.?|teaspoons?|tsp)\b/gi,           5],
];

/**
 * Convert Spanish volume units (taza, cucharada, cucharadita) → grams for metric mode.
 */
function convertSpanishToMetric(text: string): string {
  const normalized = normalizeEnglishUnits(text);
  let result = normalized;
  for (const [regex, mlPerUnit] of SPANISH_VOLUME_PATTERNS) {
    result = result.replace(regex, (match, numStr) => {
      const qty = parseNum(numStr);
      if (isNaN(qty) || qty <= 0) return match;
      const cups = (qty * mlPerUnit) / 240;
      const density = findDensity(normalized);
      const grams = cups * density.gramsPerCup;
      if (isNaN(grams) || grams <= 0) return match;
      return grams >= 10
        ? `${Math.round(grams)} g`
        : `${Math.round(grams * 10) / 10} g`;
    });
  }
  return result;
}

/**
 * Convert Spanish volume units (taza, cucharada, cucharadita, vaso) → fl oz for ounces mode.
 * In cups mode the Spanish units are kept as-is (taza/cucharada are already cup-based).
 */
function convertSpanishVolumes(text: string, targetUnit: MeasureUnit): string {
  if (targetUnit !== "ounces") return text;

  const normalized = normalizeEnglishUnits(text);
  let result = normalized;
  for (const [regex, mlPerUnit] of SPANISH_VOLUME_PATTERNS) {
    result = result.replace(regex, (match, numStr) => {
      const qty = parseNum(numStr);
      if (isNaN(qty) || qty <= 0) return match;
      const ml = qty * mlPerUnit;
      const flOz = ml / 29.5735;
      if (flOz < 0.03) return match; // truly negligible — keep original
      // Amounts < 0.5 fl oz (< ~1 tbsp) read better as tsp
      if (flOz < 0.5) {
        const tsp = ml / 4.92892;
        return `${formatFraction(tsp)} tsp`;
      }
      return `${Math.round(flOz * 10) / 10} fl oz`;
    });
  }
  return result;
}

/** Also convert ml for liquids, and Spanish volume units (taza, cucharada, etc.) */
export function convertIngredientFull(text: string, targetUnit: MeasureUnit): string {
  if (targetUnit === "metric") {
    // Convert Spanish volumetric units → grams so metric mode shows actual weights
    return convertSpanishToMetric(text);
  }

  let result = convertIngredient(text, targetUnit);

  // Normalize liters to ml first so downstream patterns can match.
  // Matches: 1 l, 1L, 1 litro, 1 liter, 1 litros, 1 liters
  const litersRegex = /(\d+(?:[.,]\d+)?)\s*(?:l|litros?|liters?)\b/gi;
  result = result.replace(litersRegex, (match, numStr) => {
    const l = parseFloat(numStr.replace(",", "."));
    if (isNaN(l) || l <= 0) return match;
    const ml = l * 1000;
    return `${ml % 1 === 0 ? ml : ml.toFixed(0)} ml`;
  });

  const mlRegex = /(\d+(?:[.,]\d+)?)\s*ml\b/gi;
  result = result.replace(mlRegex, (match, numStr) => {
    const ml = parseFloat(numStr.replace(",", "."));
    if (isNaN(ml) || ml <= 0) return match;

    switch (targetUnit) {
      case "ounces": {
        const flOz = ml / 29.5735;
        return `${Math.round(flOz * 10) / 10} fl oz`;
      }
      case "cups": {
        const cups = ml / 240;
        if (cups < 0.2) {
          const tbsp = ml / 15;
          if (tbsp < 1) {
            return `${formatFraction(tbsp * TSP_PER_TBSP)} tsp`;
          }
          return `${formatFraction(tbsp)} tbsp`;
        }
        return `${formatFraction(cups)} cup${cups > 1.1 ? "s" : ""}`;
      }
      default:
        return match;
    }
  });

  // Convert Spanish volume units (taza → fl oz, cucharada → tbsp/fl oz, etc.)
  result = convertSpanishVolumes(result, targetUnit);

  // In cups mode, clean up any "cup(s)"/"glass(es)" artifacts left by translateSpanishUnits
  if (targetUnit === "cups") {
    result = normalizeEnglishUnits(result);
  }

  return result;
}

// ─── Spanish → English unit name translation ───

const SPANISH_UNIT_MAP: [RegExp, string][] = [
  [/\bcucharaditas?\b/gi, "tsp"],
  [/\bcdtas?\b\.?/gi, "tsp"],
  [/\bcucharadas?\b/gi, "tbsp"],
  [/\bcdas?\b\.?/gi, "tbsp"],
  [/\btazas?\b/gi, "cup(s)"],
  [/\bpiezas?\b/gi, "piece(s)"],
  [/\bpizca\b/gi, "pinch"],
  [/\brebanadas?\b/gi, "slice(s)"],
  [/\bdientes?\s+de\s+ajo\b/gi, "garlic clove(s)"],
  [/\bpuñado\b/gi, "handful"],
  [/\brajitas?\b/gi, "strip(s)"],
  [/\blitros?\b/gi, "liter(s)"],
  [/\bvasos?\b/gi, "glass(es)"],
  [/\bal\s+gusto\b/gi, "to taste"],
];

/**
 * Translate Spanish measurement unit names to English equivalents.
 * Only call this for EN-language users to avoid Spanish leaking into the UI.
 */
export function translateSpanishUnits(text: string): string {
  let result = text;
  for (const [regex, replacement] of SPANISH_UNIT_MAP) {
    result = result.replace(regex, replacement);
  }
  return result;
}

/**
 * Convert temperatures bidirectionally:
 *   - imperial mode (cups / ounces): °C → °F
 *   - metric  mode:                  °F → °C
 *
 * Matched patterns (case-insensitive, optional °, optional "degrees"/"grados"):
 *   180°C · 180 °C · 180° C · 180 grados C · 180 grados Celsius · 180C
 *   350°F · 350 °F · 350 F · 350 degrees Fahrenheit · 350F
 *
 * The unit letter must NOT be followed by another letter so we don't capture
 * things like "Cilantro" or "Flour".
 */
export function convertTemperatures(text: string, targetUnit: MeasureUnit): string {
  // Step 0 — Dual-unit pairs: "X°C (Y°F)" or "X°F (Y°C)"
  // Recipes often include the other-system value in parentheses as a courtesy.
  // Handle these before single-value passes to avoid producing two values of the same unit.
  const cWithFHint = /(\d+(?:[.,]\d+)?)\s*°C\s*\(\s*\d+(?:[.,]\d+)?\s*°F\s*\)/gi;
  const fWithCHint = /(\d+(?:[.,]\d+)?)\s*°F\s*\(\s*\d+(?:[.,]\d+)?\s*°C\s*\)/gi;

  if (targetUnit === "metric") {
    text = text
      // "180°C (350°F)" → "180°C"  (already in C, drop F hint)
      .replace(cWithFHint, (_match, c) => `${c}°C`)
      // "350°F (180°C)" → "177°C"  (convert F to C, drop C hint)
      .replace(fWithCHint, (match, fStr) => {
        const f = parseFloat(String(fStr).replace(",", "."));
        if (isNaN(f)) return match;
        return `${Math.round((f - 32) * 5 / 9)}°C`;
      });
  } else {
    // "180°C (350°F)" → "356°F"  (convert C to F, drop F hint)
    text = text.replace(cWithFHint, (match, cStr) => {
      const c = parseFloat(String(cStr).replace(",", "."));
      if (isNaN(c)) return match;
      return `${Math.round(c * 9 / 5 + 32)}°F`;
    });
    // "350°F (180°C)" → "350°F"  (already in F, drop C hint)
    text = text.replace(fWithCHint, (_match, f) => `${f}°F`);
  }

  // Step 1 — Range patterns like "150–165°C" or "300–325°F"
  const celsiusRangeRegex = /(\d+(?:[.,]\d+)?)\s*[–\-]\s*(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:c(?:elsius)?)(?![a-z])/gi;
  const fahrenheitRangeRegex = /(\d+(?:[.,]\d+)?)\s*[–\-]\s*(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:f(?:ahrenheit)?)(?![a-z])/gi;
  // Step 2 — Single values
  const celsiusRegex = /(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:c(?:elsius)?)(?![a-z])/gi;
  const fahrenheitRegex = /(\d+(?:[.,]\d+)?)\s*°?\s*(?:grados?|degrees?)?\s*(?:f(?:ahrenheit)?)(?![a-z])/gi;

  if (targetUnit === "metric") {
    // F → C: ranges first, then singles
    return text
      .replace(fahrenheitRangeRegex, (match, n1, n2) => {
        const f1 = parseFloat(n1.replace(",", "."));
        const f2 = parseFloat(n2.replace(",", "."));
        if (isNaN(f1) || isNaN(f2)) return match;
        return `${Math.round((f1 - 32) * 5 / 9)}–${Math.round((f2 - 32) * 5 / 9)}°C`;
      })
      .replace(fahrenheitRegex, (match, numStr) => {
        const fahrenheit = parseFloat(numStr.replace(",", "."));
        if (isNaN(fahrenheit)) return match;
        return `${Math.round((fahrenheit - 32) * 5 / 9)}°C`;
      });
  }

  // Imperial modes (cups, ounces) — C → F: ranges first, then singles
  return text
    .replace(celsiusRangeRegex, (match, n1, n2) => {
      const c1 = parseFloat(n1.replace(",", "."));
      const c2 = parseFloat(n2.replace(",", "."));
      if (isNaN(c1) || isNaN(c2)) return match;
      return `${Math.round(c1 * 9 / 5 + 32)}–${Math.round(c2 * 9 / 5 + 32)}°F`;
    })
    .replace(celsiusRegex, (match, numStr) => {
      const celsius = parseFloat(numStr.replace(",", "."));
      if (isNaN(celsius)) return match;
      return `${Math.round(celsius * 9 / 5 + 32)}°F`;
    });
}
